import { createRESTStore } from "@stores/shared/createRESTStore";
import apiClient from "@services/api/apiClient";
import API from "@config/shared/api-paths";

// Content widget bodies are cached per widget, same TTL as the rolodex.
const CONTENT_CACHE_TTL = 5 * 60 * 1000;

const useWidgetStore = createRESTStore({
  entityName: "widgets",
  apiPaths: {
    fetch: API.BASE + API.FRONTEND.WIDGETS,
  },
  // null until the request answers: [] then means the tenant really has no widgets.
  initialState: null,
  processResponse: {
    fetch: (response) => response.items,
  },

  additionalActions: (set, get) => {
    // When each widget's content last answered 404. A 404 is kept for the same
    // TTL as a body and then asked again, since a hidden widget has no Refresh.
    let unavailableAt = {};
    // Bumped by clearContents: answers to requests made before it are dropped.
    let generation = 0;
    const isFresh = (at) => Date.now() - at < CONTENT_CACHE_TTL;

    return {
      // { [widgetId]: { content, fetchedAt } }
      contents: {},
      fetchingContent: {},
      // 404: wrong type, unpublished or not visible to this reader → hidden.
      contentUnavailable: {},
      contentFailed: {},

      // GET /client/widgets/{id}/content → { id, content } (Content widgets only).
      fetchContent: async (widgetId, forceRefresh = false) => {
        const { contents, fetchingContent, contentUnavailable } = get();
        if (fetchingContent[widgetId]) return;
        const entry = contents[widgetId];
        if (
          !forceRefresh &&
          ((contentUnavailable[widgetId] && isFresh(unavailableAt[widgetId])) ||
            (entry && isFresh(entry.fetchedAt)))
        )
          return;

        const request = generation;
        set((state) => ({
          fetchingContent: { ...state.fetchingContent, [widgetId]: true },
          contentFailed: { ...state.contentFailed, [widgetId]: false },
        }));

        try {
          const { data } = await apiClient.get(
            `${get().apiPaths.fetch}/${widgetId}/content`,
          );
          if (request !== generation) return;
          set((state) => ({
            contents: {
              ...state.contents,
              [widgetId]: { content: data?.content ?? null, fetchedAt: Date.now() },
            },
            fetchingContent: { ...state.fetchingContent, [widgetId]: false },
            contentUnavailable: { ...state.contentUnavailable, [widgetId]: false },
          }));
        } catch (error) {
          if (request !== generation) return;
          const unavailable = error?.status === 404;
          if (unavailable) unavailableAt[widgetId] = Date.now();
          set((state) => ({
            fetchingContent: { ...state.fetchingContent, [widgetId]: false },
            contentUnavailable: { ...state.contentUnavailable, [widgetId]: unavailable },
            contentFailed: { ...state.contentFailed, [widgetId]: !unavailable },
          }));
          if (!unavailable) console.error(error);
        }
      },

      // Bodies are hydrated per reader, so useWidgetCacheReset empties them
      // when the session ends.
      clearContents: () => {
        generation += 1;
        unavailableAt = {};
        set({ contents: {}, fetchingContent: {}, contentUnavailable: {}, contentFailed: {} });
      },
    };
  },
});

export default useWidgetStore;
